import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Plus, Loader2, ClipboardList, Users, FileText, Trash2, ArrowRight, Columns3,
} from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { RfqTypeBadge, RfqTypePicker } from "@/components/RfqType";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const statusPill = {
  draft: "bg-secondary text-muted-foreground",
  sent: "bg-brand-soft text-brand",
  awarded: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-200",
  closed: "bg-secondary text-muted-foreground",
};

export default function BuyerRfqs() {
  const navigate = useNavigate();
  const [rfqs, setRfqs] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ title: "", project: "", location: "", due_date: "", rfq_type: "tender" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/buyer/rfqs", { params: { kind: "rfq" } });
      setRfqs(data.rfqs);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not load your RFQs");
      setRfqs([]);
    }
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/buyer/rfqs", { ...form, items: [] });
      toast.success("RFQ created — add your line items next");
      navigate(`/rfqs/${data.id}`);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Could not create the RFQ");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id, title) => {
    if (!window.confirm(`Delete RFQ "${title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/buyer/rfqs/${id}`);
      toast.success("RFQ deleted");
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  if (!rfqs) return <div className="flex justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  return (
    <div data-testid="buyer-rfqs-page" className="space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground font-bold">Procurement</div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 dark:text-white mt-2">My RFQs</h1>
          <p className="text-sm text-muted-foreground mt-1">Request prices from vendors and compare what comes back.</p>
        </div>
        <Button data-testid="new-rfq-button" onClick={() => setCreating(!creating)} className="gap-2 font-semibold h-11">
          <Plus className="w-4 h-4" /> New RFQ
        </Button>
      </div>

      {creating && (
        <motion.form
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          onSubmit={create}
          data-testid="new-rfq-form"
          className="bg-card border border-border rounded-2xl p-6 grid sm:grid-cols-2 gap-4"
        >
          <div className="sm:col-span-2">
            <Label htmlFor="rfq-title">RFQ title</Label>
            <Input id="rfq-title" data-testid="rfq-title-input" required value={form.title}
                   onChange={(e) => setForm({ ...form, title: e.target.value })}
                   placeholder="e.g. Tower B — Aluminium & Glazing Package" className="mt-1.5" />
          </div>
          <div>
            <Label htmlFor="rfq-project">Project</Label>
            <Input id="rfq-project" data-testid="rfq-project-input" value={form.project}
                   onChange={(e) => setForm({ ...form, project: e.target.value })}
                   placeholder="e.g. Marina Heights" className="mt-1.5" />
          </div>
          <div>
            <Label htmlFor="rfq-location">Location</Label>
            <Input id="rfq-location" data-testid="rfq-location-input" value={form.location}
                   onChange={(e) => setForm({ ...form, location: e.target.value })}
                   placeholder="e.g. Dubai, UAE" className="mt-1.5" />
          </div>
          <div>
            <Label htmlFor="rfq-due">Quotes due by</Label>
            <Input id="rfq-due" type="date" data-testid="rfq-due-input" value={form.due_date}
                   onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="mt-1.5" />
          </div>
          <div>
            <Label>Enquiry type</Label>
            <div className="mt-1.5">
              <RfqTypePicker value={form.rfq_type} onChange={(v) => setForm({ ...form, rfq_type: v })} />
            </div>
            <p className="text-[11px] text-muted-foreground mt-1.5">
              Job on hand is flagged in bold in the vendor's invitation email — tenders stay unmarked.
            </p>
          </div>
          <div className="flex items-end sm:col-span-2">
            <Button type="submit" data-testid="create-rfq-button" disabled={busy} className="h-11 w-full sm:w-auto sm:px-8 font-semibold gap-2">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Create RFQ
            </Button>
          </div>
        </motion.form>
      )}

      {rfqs.length === 0 ? (
        <div data-testid="no-rfqs" className="bg-card border border-border rounded-2xl p-12 text-center">
          <ClipboardList className="w-9 h-9 text-muted-foreground mx-auto" />
          <p className="font-semibold text-slate-900 dark:text-slate-100 mt-4">No RFQs yet</p>
          <p className="text-sm text-muted-foreground mt-1">Create one, add your line items or upload a BOQ, then invite vendors.</p>
        </div>
      ) : (
        <div data-testid="rfq-list" className="space-y-3">
          {rfqs.map((r, i) => (
            <motion.div
              key={r.id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: Math.min(i * 0.04, 0.3) }}
              data-testid={`rfq-row-${i}`}
              className="bg-card border border-border rounded-2xl p-5 flex flex-col sm:flex-row sm:items-center gap-4 hover:border-primary/40 transition-colors"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <button
                    data-testid={`rfq-title-${i}`}
                    onClick={() => navigate(`/rfqs/${r.id}`)}
                    className="font-display text-lg font-bold text-slate-900 dark:text-slate-100 hover:text-primary transition-colors text-left"
                  >
                    {r.title}
                  </button>
                  {r.rfq_ref && (
                    <button
                      data-testid={`rfq-ref-${i}`}
                      onClick={() => navigate(`/rfqs/${r.id}`)}
                      className="text-xs font-mono text-muted-foreground hover:text-primary transition-colors"
                    >
                      {r.rfq_ref}
                    </button>
                  )}
                  <RfqTypeBadge type={r.rfq_type} testid={`rfq-type-${i}`} />
                  <span className={`px-2 py-0.5 rounded-md text-[11px] font-bold uppercase tracking-wide ${statusPill[r.status] || statusPill.draft}`}>
                    {r.status}
                  </span>
                </div>
                <div className="text-xs text-muted-foreground mt-1.5 flex items-center gap-4 flex-wrap">
                  {r.project && <span>{r.project}</span>}
                  <span className="inline-flex items-center gap-1"><FileText className="w-3 h-3" />{r.item_count} items</span>
                  <span className="inline-flex items-center gap-1"><Users className="w-3 h-3" />{r.invited_count} invited</span>
                  <span className="inline-flex items-center gap-1 font-semibold text-foreground">
                    <Columns3 className="w-3 h-3" />{r.quote_count} quote{r.quote_count === 1 ? "" : "s"}
                  </span>
                  {r.due_date && <span>Due {r.due_date}</span>}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {r.quote_count > 0 && (
                  <Button variant="outline" data-testid={`compare-${i}`} onClick={() => navigate(`/compare/${r.id}`)} className="h-10 gap-2">
                    <Columns3 className="w-4 h-4" /> Compare
                  </Button>
                )}
                <Button data-testid={`open-rfq-${i}`} onClick={() => navigate(`/rfqs/${r.id}`)} className="h-10 gap-2 font-semibold">
                  Open <ArrowRight className="w-4 h-4" />
                </Button>
                <button
                  data-testid={`delete-rfq-${i}`}
                  onClick={() => remove(r.id, r.title)}
                  className="w-10 h-10 rounded-lg border border-border flex items-center justify-center text-muted-foreground hover:text-destructive hover:border-destructive/40 transition-colors"
                  title="Delete RFQ"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
